import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

import { ROUTING_PATH } from '@shared/constants/routingPath';
import { CmcService } from '@shared/services/cmc.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';

enum RouterLinks {
  Clinical = 'clinicalRouterLink',
  Finance = 'financeRouterLink',
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgClass, AsyncPipe],
  selector: 'aux-trial-insights-nav',
  styleUrls: ['../../trial-insights.component.scss'],
  templateUrl: './trial-insights-nav.component.html',
})
export class TrialInsightsNavComponent implements OnInit {
  private authQuery = inject(AuthQuery);
  private cmcService = inject(CmcService);
  private readonly destroyRef = inject(DestroyRef);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private router = inject(Router);

  activeLink: RouterLinks = RouterLinks.Clinical;
  path = ROUTING_PATH.TRIAL_INSIGHTS;
  clinicalRouterLink = `/${this.path.INDEX}/${this.path.CLINICAL}`;
  clinOpsButtonFf$: Observable<boolean>;
  financeRouterLink = `/${this.path.INDEX}/${this.path.FINANCE}`;
  RouterLinks = RouterLinks;
  showClinOpsButton$ = toObservable(this.cmcService.isTabVisibleForCmc('clin_ops_dashboard'));
  showFinanceButton$: Observable<boolean>;
  userName = '';
  userOrganization = '';

  ngOnInit(): void {
    this.getUserInfo();
    const defaultVal = this.router.url.includes(RouterLinks.Finance);

    this.activeLink = defaultVal ? RouterLinks.Finance : RouterLinks.Clinical;
  }

  constructor() {
    this.showFinanceButton$ = this.launchDarklyService.select$((flags) => flags.finance_dashboard);
    this.clinOpsButtonFf$ = this.launchDarklyService.select$((flags) => flags.clin_ops_dashboard);
    combineLatest([this.clinOpsButtonFf$, this.showFinanceButton$])
      .pipe(takeUntilDestroyed())
      .subscribe(([clin_ops, finance]) => {
        if (!clin_ops && finance) {
          void this.router.navigateByUrl(this.financeRouterLink);
        }

        if (clin_ops && !finance) {
          void this.router.navigateByUrl(this.clinicalRouterLink);
        }
      });
  }

  getUserInfo(): void {
    // User header info
    this.authQuery
      .select(['given_name', 'trial_id'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ given_name }) => {
        const trial = this.mainQuery.getSelectedTrial();

        this.userName = given_name;
        this.userOrganization = trial?.sponsor_organization.name || '';
      });
  }

  isActive(link: RouterLinks): boolean {
    return this.activeLink === link;
  }

  setActive(link: RouterLinks): void {
    this.activeLink = link;
  }
}
